import React from 'react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { SectionHeading } from '@/components/sections/SectionHeading';
import { PricingCards } from '@/components/sections/PricingCards';
import { FaqAccordion } from '@/components/sections/FaqAccordion';
import { CtaBanner } from '@/components/sections/CtaBanner';
import { useResolvedPlans } from '@/hooks/usePlans';

export default function Pricing() {
  const { t } = useTranslation();
  const { plans } = useResolvedPlans();
  const faqs = t('faqs', { returnObjects: true });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Copy.Click Subscription',
    description: 'Copy.Click subscription plans for signal followers and providers, each with a 7-day free trial.',
    offers: plans
      .filter((p) => !/trial/i.test(p.name))
      .map((p) => ({
        '@type': 'Offer',
        name: p.name,
        price: Number(p.price_monthly || 0).toFixed(2),
        priceCurrency: p.currency || 'USD',
        availability: 'https://schema.org/InStock',
      })),
  };

  return (
    <>
      <Seo title={t('pricing.seoTitle')} description={t('pricing.seoDescription')} jsonLd={jsonLd} />

      <section className="relative overflow-hidden px-6 pb-8 pt-32 text-center lg:px-8 sm:pt-40">
        <div className="pointer-events-none absolute left-1/2 top-24 -z-10 h-96 w-96 -translate-x-1/2 rounded-full bg-brand/10 blur-[120px]" />
        <SectionHeading as="h1" center eyebrow={t('pricing.eyebrow')} title={t('pricing.title')} subtitle={t('pricing.subtitle')} />
      </section>

      <section className="px-6 py-14 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow={t('pricing.followersSection.eyebrow')} title={t('pricing.followersSection.title')} subtitle={t('pricing.followersSection.subtitle')} />
          <div className="mt-12"><PricingCards audience="follower" /></div>
        </div>
      </section>

      <section className="px-6 py-14 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow={t('pricing.providersSection.eyebrow')} title={t('pricing.providersSection.title')} subtitle={t('pricing.providersSection.subtitle')} />
          <div className="mt-12"><PricingCards audience="provider" /></div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-8">
        <SectionHeading eyebrow={t('pricing.faqEyebrow')} title={t('pricing.faqTitle')} />
        <div className="mx-auto mt-12 max-w-6xl"><FaqAccordion items={faqs.slice(2, 6)} /></div>
      </section>

      <CtaBanner />
    </>
  );
}
